'use client';

import { useEffect, useRef } from 'react';
import { trackStorefrontEvent, type StorefrontSurface } from '@/libs/analytics/storefrontEvents';

/** One exposure per content item per mounted surface: >=50% visible for one second. */
export function useStorefrontImpression<T extends HTMLElement = HTMLElement>(surface: StorefrontSurface, contentId: string, enabled = true, sharedSeen?: Set<string>) {
  const ref = useRef<T | null>(null);
  const localSeen = useRef(new Set<string>());
  const seen = sharedSeen ?? localSeen.current;
  useEffect(() => {
    const node = ref.current;
    const key = `${surface}:${contentId}`;
    if (!node || !enabled || seen.has(key) || typeof IntersectionObserver === 'undefined') return;
    let visible = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cancel = () => { if (timer !== undefined) clearTimeout(timer); timer = undefined; };
    const check = () => {
      const covered = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]')).some(
        dialog => !dialog.hidden && dialog.getAttribute('aria-hidden') !== 'true' && dialog.dataset.state !== 'closed'
      );
      if (!visible || document.hidden || covered || seen.has(key)) { cancel(); return; }
      if (timer !== undefined) return;
      timer = setTimeout(() => {
        timer = undefined;
        seen.add(key);
        trackStorefrontEvent('storefront_impression', { surface, content_id: contentId });
      }, 1000);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.5);
      check();
    }, { threshold: 0.5 });
    observer.observe(node);
    const dialogs = new MutationObserver(check);
    dialogs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state', 'aria-hidden', 'hidden'] });
    document.addEventListener('visibilitychange', check);
    return () => { cancel(); observer.disconnect(); dialogs.disconnect(); document.removeEventListener('visibilitychange', check); };
  }, [surface, contentId, enabled, seen]);
  return ref;
}
